/**
 * AppHeader — Top header bar of the ERP shell.
 *
 * Extracted from App.js as part of the App.js complexity reduction.
 * Responsible for:
 *   - Logo + sidebar toggle
 *   - Global search trigger (Module Launcher)
 *   - Hub / Settings / Nexus / Active Operations / Fullscreen / Ribbon
 *   - License chip + User menu (with logout)
 *
 * Company Selector + Quick Access (reports) live in the User menu (RightRibbonPanel)
 * to keep this bar uncluttered. Doc Hub is the single combined import entry point
 * (opens GlobalImportModal — Opening Balances / GL / CoA / BRS / Purchase Invoice /
 * Journal / Generic); the separate header "Import" dropdown was retired.
 */
import React, { useState } from 'react';
import {
  Sparkles, PanelLeftClose, PanelLeft, Search,
  LayoutGrid, Settings, Maximize, Minimize, Zap, ScanLine,
} from 'lucide-react';
import { ModuleId } from '@/types';
import SettingsDropdown from '@/components/header/SettingsDropdown';
import { HeaderUserInfo } from '@/components/header/UserMenu';
import { LicenseHeaderChip } from '@/components/header/LicenseHeaderChip';

const ACTIVE_OPS = [
  { label: 'Day Opening', screen: 'DAY_OPEN' },
  { label: 'Day Closing', screen: 'DAY_CLOSE' },
];
const QUICK_ACCESS = [
  { label: 'Product Master (Old)', screen: 'ITEM_CREATION' },
  { label: 'Product Master (New)', screen: 'ITEM_CREATION_NEW' },
  { label: 'Item Master List', screen: 'ITEM_LIST' },
  { label: 'Industry Config', screen: 'INDUSTRY_CONFIG' },
  { label: 'Module Management', screen: 'MODULE_MASTER' },
  { label: 'User Management', screen: 'USER_PRIVILEGES' },
  { label: 'Dynamic Attributes', screen: 'DYNAMIC_ATTRIBUTES' },
  { label: 'Help Engine', screen: 'HELP_ENGINE' },
  { label: 'Tier-Privilege Matrix', screen: 'TIER_PRIVILEGE_MATRIX' },
];

export default function AppHeader({
  theme,
  isDark,
  sidebarExpanded,
  setSidebarExpanded,
  fontSize, setFontSize,
  language, setLanguage,
  currency, setCurrency,
  onToggleTheme,
  isFullscreen, onToggleFullscreen,
  onOpenLauncher,
  navigateTo,
  isNexusOpen, onToggleNexus,
  isRightRibbonOpen, onToggleRightRibbon,
  onLogout,
  onOpenDocHub,
  userRole,
}) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isActiveMenuOpen, setIsActiveMenuOpen] = useState(false);

  return (
    <header data-testid="app-header" className="app-header h-11 flex items-center px-2 sm:px-3 border-b shrink-0 z-50 gap-2 shadow-sm">
      {/* Logo */}
      <div className="flex items-center gap-2 shrink-0" data-testid="header-logo">
        <img src="/retailcliq-logo.png" alt="RetailCLIQ" className="h-7" />
      </div>

      {/* Sidebar toggle */}
      <button data-testid="toggle-sidebar-btn" onClick={() => setSidebarExpanded((p) => !p)}
        className={`p-1.5 rounded-lg transition-colors shrink-0 ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}>
        {sidebarExpanded ? <PanelLeftClose size={17} /> : <PanelLeft size={17} />}
      </button>

      <div className="flex-1" />

      {/* Global search */}
      <button data-testid="launcher-trigger-btn" onClick={onOpenLauncher}
        className={`hidden sm:flex items-center gap-2 px-4 py-1.5 rounded-lg border text-[11px] font-semibold transition-all min-w-[200px] md:min-w-[280px] lg:min-w-[320px] justify-center ${isDark ? 'bg-[#151515] border-[#2a2a2a] text-slate-500 hover:text-white hover:border-blue-500/50' : 'bg-slate-50 border-slate-200 text-slate-400 hover:text-slate-700 hover:border-blue-400'}`}>
        <Search size={14} />
        <span className="hidden sm:inline">Search modules...</span>
        <kbd className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-mono border hidden md:inline ${isDark ? 'bg-[#222] border-[#333] text-slate-500' : 'bg-white border-slate-200 text-slate-400'}`}>Ctrl+K</kbd>
      </button>

      <div className="flex-1" />

      {/* Hub */}
      <button data-testid="hub-trigger-btn" onClick={() => navigateTo(ModuleId.HUB)}
        className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`} title="Central Hub">
        <LayoutGrid size={17} />
      </button>

      {/* Settings */}
      <div className="relative">
        <button data-testid="settings-btn" onClick={() => setIsSettingsOpen((p) => !p)}
          className={`p-1.5 rounded-lg transition-colors ${isSettingsOpen ? 'bg-blue-600 text-white' : isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`} title="Settings">
          <Settings size={17} />
        </button>
        <SettingsDropdown isOpen={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} theme={theme}
          fontSize={fontSize} setFontSize={setFontSize} language={language} setLanguage={setLanguage} currency={currency} setCurrency={setCurrency}
          userRole={userRole} />
      </div>

      {/* Nexus AI */}
      <button data-testid="nexus-toggle-btn" onClick={onToggleNexus}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-wide transition-all ${isNexusOpen ? 'bg-blue-600 text-white border-blue-600 shadow shadow-blue-500/20' : isDark ? 'bg-[#151515] border-[#2a2a2a] text-slate-400 hover:text-white' : 'bg-slate-50 border-slate-200 text-slate-500 hover:text-slate-700'}`}>
        <Sparkles size={13} /><span className="hidden lg:inline">Nexus</span>
      </button>

      {/* Document Hub — single combined import entry point (OCR/CSV: Opening Balances, GL, CoA, BRS, Purchase Invoice, Journal, Generic) */}
      <button data-testid="doc-hub-trigger-btn" onClick={onOpenDocHub}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-wide transition-all ${isDark ? 'bg-[#151515] border-[#2a2a2a] text-purple-400 hover:text-white hover:border-purple-500/50' : 'bg-slate-50 border-slate-200 text-purple-600 hover:text-purple-700 hover:border-purple-400'}`}
        title="Doc Hub — Import Opening Balances, GL, Chart of Accounts, BRS, Purchase Invoice, Journal Entry (CSV or AI/PDF)">
        <ScanLine size={13} /><span className="hidden lg:inline">Doc Hub</span>
      </button>

      {/* Active Buttons Menu */}
      <div className="relative">
        <button data-testid="active-buttons-btn" onClick={() => setIsActiveMenuOpen(p => !p)}
          className={`p-1.5 rounded-lg transition-colors ${isActiveMenuOpen ? 'bg-orange-500 text-white' : isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`} title="Active Operations">
          <Zap size={17} />
        </button>
        {isActiveMenuOpen && (
          <div data-dropdown-panel className={`dropdown-panel absolute right-0 top-full mt-1 w-48 rounded-lg border shadow-lg z-50 ${isDark ? 'bg-[#1a1a1a] border-[#333]' : ''}`} data-testid="active-buttons-menu">
            <div className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider border-b ${isDark ? 'text-orange-400 border-[#333]' : 'text-orange-600 border-slate-100'}`}>Active Operations</div>
            {ACTIVE_OPS.map(item => (
              <button key={item.screen} onClick={() => { navigateTo(item.screen); setIsActiveMenuOpen(false); }} className={`w-full text-left px-3 py-2 text-xs transition-colors ${isDark ? 'text-slate-300 hover:bg-white/5' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`active-${item.screen}`}>{item.label}</button>
            ))}
            <div className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider border-b border-t ${isDark ? 'text-blue-400 border-[#333]' : 'text-blue-600 border-slate-100'}`}>Quick Access</div>
            {QUICK_ACCESS.map(item => (
              <button key={item.screen} onClick={() => { navigateTo(item.screen); setIsActiveMenuOpen(false); }} className={`w-full text-left px-3 py-2 text-xs transition-colors ${isDark ? 'text-slate-300 hover:bg-white/5' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`quick-${item.screen}`}>{item.label}</button>
            ))}
          </div>
        )}
      </div>

      {/* Theme toggle — removed (available in User Panel) */}

      {/* Fullscreen toggle */}
      <button data-testid="fullscreen-btn" onClick={onToggleFullscreen}
        className={`p-1.5 rounded-lg transition-colors hidden md:block ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`} title="Toggle Fullscreen">
        {isFullscreen ? <Minimize size={17} /> : <Maximize size={17} />}
      </button>

      <div className={`w-px h-5 mx-0.5 shrink-0 ${isDark ? 'bg-[#333]' : 'bg-slate-200'}`} />

      <LicenseHeaderChip theme={theme} />

      {/* User avatar — opens combined User + Theme Panel (RightRibbonPanel) */}
      <div className="relative shrink-0">
        <HeaderUserInfo
          onClick={() => { onToggleRightRibbon(); setIsSettingsOpen(false); }}
          isOpen={isRightRibbonOpen}
          theme={theme}
        />
      </div>
    </header>
  );
}
